import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Layout from './components/Layout';
import AccountList from './components/AccountList';
import AccountDetails from './components/AccountDetails';
import QuestionnaireList from './components/QuestionnaireList';
import AnswerPage from './components/AnswerPage';
import LeadList from './components/LeadList';
import LeadDetails from './components/LeadDetails';
import AgencySubmissionsList from './components/AgencySubmissionsList';
import ProjectList from './components/ProjectList';
import ProjectDetails from './components/ProjectDetails';
import TimeTracker from './components/TimeTracker';
import InteractionList from './components/InteractionList';
import ContactList from './components/ContactList';
import ContactDetails from './components/ContactDetails';
import UserList from './components/UserList';
import ContractList from './components/ContractList';
import Files from './components/Files';
import Marketing from './components/Marketing';
import SocialMediaGenerator from './components/SocialMediaGenerator';
import TeamCalendar from './components/TeamCalendar';
import Homepage from './components/Homepage';

import axiosInstance from './components/axiosInstance';
import { SnackbarProvider } from "./providers/SnackbarProvider";
import { PipelineConfigProvider } from "./providers/PipelineConfigProvider";


import BreadcrumbBar, { BreadcrumbProvider } from './components/BreadcrumbBar';

// Styles für das Dashboard
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const PrivateRoute = ({ children }) => {
  const isAuthenticated = !!localStorage.getItem('access_token');
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const App = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const response = await axiosInstance.get('/me/');
          setUser(response.data);
        } catch (error) {
          console.error("Error fetching user:", error);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setUser(null);
    window.location.href = '/login';
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Router>
      <SnackbarProvider>
        <PipelineConfigProvider>
        <BreadcrumbProvider>
          {user ? (
            <Layout user={user} onLogout={handleLogout}>
              <BreadcrumbBar />
                <Routes>
                  <Route path="/login" element={<Navigate to="/" />} />
                  <Route path="/" element={<PrivateRoute><Homepage /></PrivateRoute>} />
                  <Route path="/accounts" element={<PrivateRoute><AccountList /></PrivateRoute>} />
                  <Route path="/accounts/:accountId" element={<PrivateRoute><AccountDetails /></PrivateRoute>} />
                  <Route path="/questionnaires" element={<PrivateRoute><QuestionnaireList /></PrivateRoute>} />
                  <Route path="/questionnaires/:id/answer" element={<PrivateRoute><AnswerPage /></PrivateRoute>} />
                  <Route path="/leads" element={<PrivateRoute><LeadList /></PrivateRoute>} />
                  <Route path="/leads/:id" element={<PrivateRoute><LeadDetails /></PrivateRoute>} />
                  <Route path="/agency-submissions" element={<PrivateRoute><AgencySubmissionsList /></PrivateRoute>} />
                  <Route path="/projects" element={<PrivateRoute><ProjectList /></PrivateRoute>} />
                  <Route path="/projects/:projectId/" element={<PrivateRoute><ProjectDetails /></PrivateRoute>} />
                  <Route path="/timetracker" element={<PrivateRoute><TimeTracker /></PrivateRoute>} />
                  <Route path="/interactions" element={<PrivateRoute><InteractionList /></PrivateRoute>} />
                  <Route path="/contacts" element={<PrivateRoute><ContactList /></PrivateRoute>} />
                  <Route path="/contacts/:id" element={<PrivateRoute><ContactDetails /></PrivateRoute>} />
                  <Route path="/users" element={<PrivateRoute><UserList /></PrivateRoute>} />
                  <Route path="/contracts" element={<ContractList />} />
                  <Route path="/files" element={<Files />} />
                  <Route path="/marketing" element={<PrivateRoute><Marketing /></PrivateRoute>} />
                  <Route path="/marketing/post-generator" element={<PrivateRoute><SocialMediaGenerator /></PrivateRoute>} />
                  <Route path="/team-calendar" element={<PrivateRoute><TeamCalendar /></PrivateRoute>} />
                </Routes>
            </Layout>
          ) : (
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
              <Route path="*" element={<Navigate to="/login" />} />
            </Routes>
          )}
        </BreadcrumbProvider>
        </PipelineConfigProvider>
      </SnackbarProvider>
    </Router>
  );
};

export default App;
